    import React, { useState, useEffect } from 'react';
    import { useNavigate } from 'react-router-dom';
    import "./RelatorioVendas.css";

    import {
    Button,
    TextField,
    Grid,
    Typography,
    Alert,
    Box,
    Card,
    CardContent,
    Container
    } from '@mui/material';
    import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
    import dayjs from 'dayjs';
    import api from '../../../services/api';
    import API_CONFIG from '../../../config/api.config';

    const RelatorioVendas = () => {
    const navigate = useNavigate();
    const [isMenuExpanded, setIsMenuExpanded] = useState(false);
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
    const [fornecedorId, setFornecedorId] = useState('');

    // Verificação de autenticação e tipo de usuário
    useEffect(() => {
        const tipoUsuario = localStorage.getItem('tipoUsuario');
        const idUsuario = localStorage.getItem('idUsuario');

        if (!tipoUsuario || !idUsuario) {
       //console.log('Usuário não autenticado - Redirecionando para login');
        navigate('/', { replace: true });
        return;
        }

        if (tipoUsuario !== 'FORNECEDOR') {
       //console.log(`Tipo de usuário (${tipoUsuario}) não corresponde ao permitido (FORNECEDOR)`);
        navigate('/', { replace: true });
        return;
        }

        // Pegar o ID do fornecedor do localStorage
        const fornecedorIdFromStorage = localStorage.getItem('idFornecedor');
        if (fornecedorIdFromStorage) {
        setFornecedorId(fornecedorIdFromStorage);
        }

       //console.log('Fornecedor autenticado:', idUsuario);
    }, [navigate]);

    useEffect(() => {
        const handleMenuChange = (event) => {
        setIsMenuExpanded(!event.detail.isCollapsed);
        };
        window.addEventListener('menuStateChange', handleMenuChange);
        return () => window.removeEventListener('menuStateChange', handleMenuChange);
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 600);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleLimparFiltros = () => {
        setDataInicio('');
        setDataFim('');
        setError('');
        setSuccess('');
    };

    const handleDownloadPDF = async () => {
        try {
        setLoading(true);
        setError('');
        setSuccess('');
        
        const params = {};
        if (dataInicio) params.data_inicio = dataInicio;
        if (dataFim) params.data_fim = dataFim;
        if (fornecedorId) params.FOR_ID = fornecedorId;

        const response = await api.get(API_CONFIG.ENDPOINTS.RELATORIOS.VENDAS_FOR, {
            params,
            responseType: 'blob'
        });

        // Verificar se a resposta é um PDF válido
        if (response.data.type && response.data.type.includes('application/pdf')) {
            // Criar URL do blob
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            
            // Detectar se o navegador consegue renderizar PDFs
            const canRenderPDF = () => {
                // Chrome, Edge, Firefox modernos conseguem renderizar PDFs
                const userAgent = navigator.userAgent.toLowerCase();
                return userAgent.includes('chrome') || 
                       userAgent.includes('edge') || 
                       userAgent.includes('firefox');
            };

            if (canRenderPDF()) {
                // Tentar abrir em nova aba
                const newWindow = window.open(url, '_blank');
                
                if (newWindow) {
                    // Aguardar um pouco para ver se conseguiu abrir
                    setTimeout(() => {
                        if (newWindow.closed) {
                            // Se a janela foi fechada rapidamente, provavelmente não conseguiu renderizar
                            downloadPDF(url);
                        }
                    }, 1000);
                    
                    setSuccess('Relatório aberto em nova aba!');
                } else {
                    // Popup bloqueado, fazer download
                    downloadPDF(url);
                    setSuccess('Relatório baixado com sucesso! (popup bloqueado)');
                }
            } else {
                // Navegadores que não conseguem renderizar PDFs, fazer download direto
                downloadPDF(url);
                setSuccess('Relatório baixado com sucesso!');
            }
            
            // Limpar URL após um tempo
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 5000);
        } else {
            // Se não for PDF, tentar ler como JSON para pegar mensagem de erro
            const reader = new FileReader();
            reader.onload = () => {
            try {
                const errorData = JSON.parse(reader.result);
                setError(errorData.message || 'Erro ao gerar relatório');
            } catch {
                setError('Erro ao gerar relatório PDF');
            }
            };
            reader.readAsText(response.data);
        }
        } catch (error) {
        //console.error('Erro ao baixar PDF:', error);
        
        if (error.response?.data?.message) {
            setError(error.response.data.message);
        } else if (error.response?.data) {
            // Se data for um objeto, tentar converter para string
            if (typeof error.response.data === 'object') {
            setError('Erro ao gerar relatório PDF');
            } else {
            setError(String(error.response.data));
            }
        } else {
            setError('Erro ao gerar relatório PDF');
        }
        } finally {
        setLoading(false);
        }
    };

    // Função auxiliar para fazer download do PDF
    const downloadPDF = (url) => {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `relatorio-vendas-${new Date().toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    return (
        <div className={`relatorio-vendas-container ${isMenuExpanded ? 'menu-expanded' : ''}`}>
        <div className="relatorio-vendas-wrapper">
            <h1 className="relatorio-vendas-title">Relatório de Vendas</h1>
            
            <Container maxWidth="lg" className="relatorio-vendas-content">
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                {success}
                </Alert>
            )}

            <Card className="relatorio-vendas-card">
                <CardContent>
                <Typography variant="h6" gutterBottom className="relatorio-vendas-subtitle">
                    Filtros do Relatório
                </Typography>
                
                <Grid container spacing={3} sx={{ mb: 4 }} justifyContent="center" alignItems="center">
                    <Grid item xs={12} sm={6} md={2.5}>
                    <TextField
                        label="Data Início"
                        type="date"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={dataInicio}
                        onChange={(e) => setDataInicio(e.target.value)}
                    />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.5}>
                    <TextField
                        label="Data Fim"
                        type="date"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={dataFim}
                        onChange={(e) => setDataFim(e.target.value)}
                    />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.2}>
                    <Button
                        variant="outlined"
                        onClick={handleLimparFiltros}
                        disabled={loading}
                        size="large"
                        >
                        Limpar Filtros
                    </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                    <Button
                        variant="contained"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={handleDownloadPDF}
                        disabled={loading}
                        size="large"
                        className="relatorio-vendas-export-button"
                        fullWidth
                        >
                        {loading ? 'Gerando PDF...' : 'Abrir Relatório PDF'}
                    </Button>
                    </Grid>
                </Grid>
                </CardContent>
            </Card>

            <Card className="relatorio-vendas-info-card">
                <CardContent>
                <Typography variant="h6" gutterBottom>
                    Informações do Relatório
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Este relatório permite gerar um documento PDF com todas as vendas realizadas pelo fornecedor, 
                    filtradas por período. O relatório inclui informações detalhadas sobre cada venda, incluindo 
                    valores, status e informações da prefeitura.
                </Typography>
                </CardContent>
            </Card>
            </Container>
        </div>
        </div>
    );
    };

    export default RelatorioVendas;